import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface ConfirmDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Name of the item shown in quotes under the default prompt. */
  fileName?: string;
  isLoading?: boolean;
  /** Dialog heading. Defaults to "Confirm Deletion". */
  title?: string;
  /** Replaces the default "Are you sure you want to delete this file?" body. */
  message?: React.ReactNode;
  /** Red warning line under the body. Defaults to "This action cannot be undone." */
  warningText?: string;
  /** Confirm button label. Defaults to "Delete". */
  confirmLabel?: string;
  /** Confirm button label while loading. Defaults to "Deleting...". */
  loadingLabel?: string;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  fileName,
  isLoading = false,
  title = 'Confirm Deletion',
  message,
  warningText = 'This action cannot be undone.',
  confirmLabel = 'Delete',
  loadingLabel = 'Deleting...',
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {message ?? (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this file?
              </Typography>
              {fileName && (
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                  "{fileName}"
                </Typography>
              )}
            </>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {warningText}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          color="error"
        >
          {isLoading ? loadingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteModal;
